// src/pages/GraficosArrecad.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList, Legend,
} from 'recharts';
import { Paper, Typography } from '@mui/material';
import { getMensalCache } from '../api/arrecadacao';

// =============== helpers & consts (fora dos hooks) ===============
const MONTH_LABELS = ['JAN','FEV','MAR','ABR','MAI','JUN','JUL','AGO','SET','OUT','NOV','DEZ'];
const KEYS = ['abt','comum','esco_grat','escolar','isentos','p_social','pagante','vt'];

const fmtBRL = (n) =>
  (n ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const labelUpper = (ym) => {
  const [y, m] = String(ym).split('-');
  const i = Math.max(1, Math.min(12, parseInt(m, 10))) - 1;
  return `${MONTH_LABELS[i]}/${y}`;
};

// inteiro (sem R$) – usado nos rótulos de PASSAGEIROS
const fmtInt = (n) => (n ?? 0).toLocaleString('pt-BR');

// ----------------- HELPERS DE RÓTULO – ARRECADAÇÃO (com R$) -----------------

// rótulo do valor da barra PREV (ARRECADAÇÃO): em R$
const MoneyPrevValueLabel = ({ x, y, value, payload, mode }) => {
  if (value == null || x == null || y == null) return null;
  const total = Number(payload?.total ?? 0);
  const prev  = Number(value ?? 0);
  const isHigher = prev >= total;
  const dy = isHigher ? -18 : 14; // acima/abaixo

  const stroke = mode === 'dark' ? 'rgba(0,0,0,.55)' : 'rgba(255,255,255,.7)';
  const fill   = mode === 'dark' ? '#e2e8f0' : '#0f172a';

  return (
    <>
      <text
        x={x}
        y={y + dy}
        textAnchor="middle"
        fontSize={11}
        fontWeight={800}
        stroke={stroke}
        strokeWidth={3}
        paintOrder="stroke"
        fill="transparent"
      >
        {fmtBRL(prev)}
      </text>
      <text
        x={x}
        y={y + dy}
        textAnchor="middle"
        fontSize={11}
        fontWeight={800}
        fill={fill}
      >
        {fmtBRL(prev)}
      </text>
    </>
  );
};

// ----------------- HELPERS DE RÓTULO – PASSAGEIROS (sem R$) -----------------

const PassengerPrevValueLabel = ({ x, y, value, payload, mode }) => {
  if (value == null || x == null || y == null) return null;
  const total = Number(payload?.total ?? 0);
  const prev  = Number(value ?? 0);
  const isHigher = prev >= total;
  const dy = isHigher ? -18 : 14;
  const color = mode === 'dark' ? '#ffffff' : '#111111';
  return (
    <text
      x={x}
      y={y + dy}
      textAnchor="middle"
      fontSize={13}
      fontWeight={800}
      fill={color}
    >
      {fmtInt(prev)}
    </text>
  );
};



const PassengerTotalValueLabel = ({ x, y, value, payload, mode }) => {
  if (value == null || x == null || y == null) return null;
  const prev  = Number(payload?.prev ?? 0);
  const curr  = Number(value ?? 0);
  const isHigher = curr > prev;
  const dy = isHigher ? -18 : 14;
  const color = mode === 'dark' ? '#ffffff' : '#111111';
  return (
    <text
      x={x}
      y={y + dy}
      textAnchor="middle"
      fontSize={13}
      fontWeight={800}
      fill={color}
    >
      {fmtInt(curr)}
    </text>
  );
};

// % acima da barra PREV (só quando ela é a maior)
const PassengerPrevPctLabel = ({ x, y, payload }) => {
  const prev  = Number(payload?.prev ?? 0);
  const total = Number(payload?.total ?? 0);
  if (prev <= 0 || prev < total) return null;
  const pct = ((total - prev) / (prev || 1)) * 100;
  const txt = `${pct > 0 ? '+' : ''}${pct.toFixed(2)}%`;
  return (
    <text
      x={x}
      y={y - 24}                    // antes: y - 40
      textAnchor="middle"
      fontSize={12}
      fontWeight={800}
      fill={pct > 0 ? '#12B76A' : pct < 0 ? '#F04438' : '#94a3b8'}
    >
      {txt}
    </text>
  );
};

// % acima da barra TOTAL (só quando ela é a maior)
const PassengerTotalPctLabel = ({ x, y, payload }) => {
  const total = Number(payload?.total ?? 0);
  const prev  = Number(payload?.prev ?? 0);
  if (total <= 0 || total <= prev) return null;
  const pct = ((total - prev) / (prev || 1)) * 100;
  const txt = `${pct > 0 ? '+' : ''}${pct.toFixed(2)}%`;
  return (
    <text
      x={x}
      y={y - 24}                    // antes: y - 40
      textAnchor="middle"
      fontSize={12}
      fontWeight={800}
      fill={pct > 0 ? '#12B76A' : pct < 0 ? '#F04438' : '#94a3b8'}
    >
      {txt}
    </text>
  );
};

// rótulo do valor do mês ATUAL (ARRECADAÇÃO, com leve “contorno”)
const CurrValueLabel = ({ x, y, value, mode, payload }) => {
  if (value == null || x == null || y == null) return null;
  const prev = Number(payload?.prev ?? 0);
  const curr = Number(value ?? 0);

  const yOff = curr >= prev ? 34 : 18;

  const maxV = Math.max(prev, curr) || 1;
  const near = Math.abs(prev - curr) / maxV <= 0.01;
  const xShift = near ? +8 : 0;

  const fill = mode === 'dark' ? '#e2e8f0' : '#0f172a';

  return (
    <>
      <text
        x={x + xShift}
        y={y - yOff}
        textAnchor="middle"
        fontSize={11}
        fontWeight={800}
        stroke={mode === 'dark' ? 'rgba(0,0,0,.55)' : 'rgba(255,255,255,.7)'}
        strokeWidth={3}
        paintOrder="stroke"
        fill="transparent"
      >
        {fmtBRL(curr)}
      </text>
      <text
        x={x + xShift}
        y={y - yOff}
        textAnchor="middle"
        fontSize={11}
        fontWeight={800}
        fill={fill}
      >
        {fmtBRL(curr)}
      </text>
    </>
  );
};

// porcentagem de crescimento — afastada dos valores para não sobrepor
const GrowthLabel = ({ x, y, value }) => {
  if (value == null) return null;
  const fill = value > 0 ? '#16a34a' : value < 0 ? '#dc2626' : '#94a3b8';
  const txt = `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;
  return (
    <text
      x={x}
      y={y - 22}         // antes era -6 ou -20; agora bem mais alto
      textAnchor="middle"
      fontSize={12}
      fontWeight={800}
      fill={fill}
    >
      {txt}
    </text>
  );
};

// Tooltip SOMENTE do gráfico de ARRECADAÇÃO (com R$)
const MoneyTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const tipos = payload[0]?.payload?.tipos || {};
  const items = Object.entries(tipos)
    .filter(([, v]) => (v || 0) > 0)
    .sort((a, b) => b[1] - a[1]);

  return (
    <Paper sx={{ p: 1.25 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: .5 }}>
        {labelUpper(label)}
      </Typography>
      {items.length ? items.map(([k, v]) => (
        <Typography key={k} variant="body2">
          {k.toUpperCase()}: {fmtBRL(v)}
        </Typography>
      )) : (
        <Typography variant="body2">Sem arrecadação</Typography>
      )}
    </Paper>
  );
};

// Tooltip SOMENTE do gráfico de PASSAGEIROS (sem R$)
const CountTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const tipos = payload[0]?.payload?.tipos || {};
  const items = Object.entries(tipos)
    .filter(([, v]) => (v || 0) > 0)
    .sort((a, b) => b[1] - a[1]);

  return (
    <Paper sx={{ p: 1.25 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: .5 }}>
        {labelUpper(label)}
      </Typography>
      {items.length ? items.map(([k, v]) => (
        <Typography key={k} variant="body2">
          {k.toUpperCase()}: {Number(v || 0).toLocaleString('pt-BR')}
        </Typography>
      )) : (
        <Typography variant="body2">Sem passageiros</Typography>
      )}
    </Paper>
  );
};

// normaliza nome de tipo vindo do banco/API
const normTipo = (t) => String(t || '')
  .toLowerCase()
  .replace(/\s+/g, '_')
  .replace('.', '');

// =============== HISTÓRICO DE ARRECADAÇÃO ===============
export default function GraficosArrecad({ mode, garage, dateIni, tipoSelecionado }) {
  // yearRows: [{ ym:'YYYY-MM', total:Number, tipos:{ [k in KEYS]: Number } }]
  const [yearRows, setYearRows] = useState([]);
  const [baseYear, setBaseYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 1) Carrega e agrega por mês
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        let y = new Date().getFullYear();
        if (dateIni) {
          const m = String(dateIni).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
          if (m) y = parseInt(m[3], 10);
          else {
            const d = new Date(dateIni);
            if (!Number.isNaN(d)) y = d.getFullYear();
          }
        }
        setBaseYear(y);

        const raw = await getMensalCache({
          garage,
          ano: y,
          tipo: tipoSelecionado || ''
        });

        const acc = new Map();
        for (const r of (raw || [])) {
          const ym = `${y}-${String(r.mes).padStart(2, '0')}`;
          const k = normTipo(r.tipo);
          if (!KEYS.includes(k)) continue;

          const cur = acc.get(ym) || {
            ym,
            total: 0,
            tipos: Object.fromEntries(KEYS.map(k2 => [k2, 0])),
          };

          const v = Number(r.valor || 0);
          cur.tipos[k] += v;
          cur.total    += v;
          acc.set(ym, cur);
        }

        const aggregated = Array.from(acc.values()).sort((a, b) => a.ym.localeCompare(b.ym));

        if (!active) return;
        setYearRows(aggregated);
      } catch (err) {
        if (!active) return;
        console.error('[arrec][mensalDB] erro', err);
        setError(err?.message || 'Erro ao carregar dados');
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => { active = false; };
  }, [garage, dateIni, tipoSelecionado]);

  // 2) Monta série com meses faltantes + prev/growth
  const data = useMemo(() => {
    const y = baseYear;
    const isCurrentYear = y === new Date().getFullYear();
    const lastMonth = isCurrentYear ? new Date().getMonth() + 1 : 12;

    const months = Array.from({ length: lastMonth }, (_, i) => `${y}-${String(i + 1).padStart(2, '0')}`);

    const map = new Map();
    for (const r of yearRows) {
      if (!r?.ym) continue;
      if (String(r.ym).slice(0, 4) !== String(y)) continue;
      map.set(r.ym, { mes: r.ym, total: Number(r.total || 0), tipos: r.tipos || {} });
    }

    const arr = months.map((m) => {
      if (map.has(m)) return { mes: m, ...(map.get(m)) };
      return { mes: m, total: 0, tipos: Object.fromEntries(KEYS.map(k => [k, 0])) };
    });

    return arr.map((it, i) => {
      const prev = i > 0 ? arr[i - 1].total : null;
      const growth = prev && prev !== 0 ? ((it.total - prev) / prev) * 100 : null;
      return { ...it, prev, growth };
    });
  }, [yearRows, baseYear]);

  return (
    <Paper
      sx={{
        gridColumn: '1 / -1',
        p: 3,
        borderRadius: 3,
        boxShadow: '0px 10px 15px -3px rgba(0,0,0,0.18)',
        height: 420,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography
        variant="h5"
        sx={{ fontWeight: 900, textAlign: 'center', textTransform: 'uppercase', mb: 2 }}
      >
        HISTÓRICO DE ARRECADAÇÃO
      </Typography>

      {loading && (
        <Typography sx={{ textAlign: 'center', mb: 1, opacity: 0.8 }}>
          Carregando dados de arrecadação…
        </Typography>
      )}
      {!loading && error && (
        <Typography color="error" sx={{ textAlign: 'center', mb: 1 }}>
          {error}
        </Typography>
      )}

      <ResponsiveContainer width="100%" height="85%">
        <BarChart
          key={`${baseYear}-${data.length}`}
          data={data}
          margin={{ top: 48, right: 28, left: 16, bottom: 8 }}
          barCategoryGap="28%"
          barGap={6}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="mes"
            tickFormatter={labelUpper}
            tick={{ fontSize: 12, fill: mode === 'dark' ? '#e2e8f0' : '#0f172a' }}
          />
          <YAxis
            tickFormatter={fmtBRL}
            domain={[0, (max) => (max || 0) * 1.1]}
            width={90}
            tick={{ fontSize: 12, fill: mode === 'dark' ? '#e2e8f0' : '#0f172a' }}
          />
          <Tooltip
            content={<MoneyTooltip />}
            cursor={false}
            wrapperStyle={{ background: 'transparent', border: 'none', boxShadow: 'none' }}
          />
          <Legend
            verticalAlign="top"
            align="right"
            iconType="circle"
            iconSize={8}
            wrapperStyle={{ top: 0 }}
            formatter={(v) => <span style={{ fontSize: 12 }}>{v}</span>}
          />

          {/* ESQUERDA: mês anterior */}
          <Bar dataKey="prev" name="MÊS ANTERIOR" fill="#90caf9" radius={[6, 6, 0, 0]}>
            <LabelList dataKey="prev" content={(p) => <MoneyPrevValueLabel {...p} mode={mode} />} />
          </Bar>

          {/* DIREITA: mês atual */}
          <Bar dataKey="total" name="MÊS ATUAL" fill="#1e88e5" radius={[6, 6, 0, 0]}>
            <LabelList dataKey="growth" content={(p) => <GrowthLabel {...p} />} />
            <LabelList dataKey="total" content={(p) => <CurrValueLabel {...p} mode={mode} />} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
}

/* =========================
   HISTÓRICO DE PASSAGEIROS
   ========================= */
export function GraficosPassageiros({ mode, garage, dateIni, tipoSelecionado }) {
  const [rowsRaw, setRowsRaw] = useState([]);
  const [baseYear, setBaseYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 1) Carrega (qtd)
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        let y = new Date().getFullYear();
        if (dateIni) {
          const m = String(dateIni).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
          if (m) y = parseInt(m[3], 10);
          else {
            const d = new Date(dateIni);
            if (!Number.isNaN(d)) y = d.getFullYear();
          }
        }
        setBaseYear(y);

        const cacheKey = ['pax:mensalDB', `g=${garage ?? ''}`, `year=${y}`, `tipo=${tipoSelecionado || ''}`].join('|');
        try {
          const cached = sessionStorage.getItem(cacheKey);
          if (cached) {
            const parsed = JSON.parse(cached);
            if (Array.isArray(parsed)) {
              if (!active) return;
              setRowsRaw(parsed);
              setLoading(false);
              return;
            }
          }
        } catch {}

        const list = await getMensalCache({
          garage,
          ano: y,
          tipo: tipoSelecionado || ''
        });

        if (!active) return;
        setRowsRaw(Array.isArray(list) ? list : []);
        try { sessionStorage.setItem(cacheKey, JSON.stringify(list || [])); } catch {}
      } catch (e) {
        if (!active) return;
        setError(e?.message || 'Erro ao carregar passageiros.');
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => { active = false; };
  }, [garage, dateIni, tipoSelecionado]);

  // 2) Agrega por mês (qtd)
  const data = useMemo(() => {
    const y = baseYear;
    const isCurrentYear = y === new Date().getFullYear();
    const lastMonth = isCurrentYear ? new Date().getMonth() + 1 : 12;

    const months = Array.from({ length: lastMonth }, (_, i) => `${y}-${String(i + 1).padStart(2, '0')}`);

    const map = new Map();
    for (const r of rowsRaw) {
      const m = Number(r?.mes || 0);
      if (!m || m < 1 || m > 12) continue;
      const ym = `${y}-${String(m).padStart(2, '0')}`;

      if (!map.has(ym)) map.set(ym, { mes: ym, total: 0, tipos: {} });
      const cur = map.get(ym);

      const tipo = String(r?.tipo || '').toLowerCase();
      const q = Number(r?.qtd || 0);

      if (tipoSelecionado) {
        const t = String(tipoSelecionado).toLowerCase();
        if (tipo === t) {
          cur.total += q;
          cur.tipos[t] = (cur.tipos[t] || 0) + q;
        }
      } else {
        cur.total += q;
        cur.tipos[tipo] = (cur.tipos[tipo] || 0) + q;
      }
    }

    const arr = months.map((m) => map.get(m) || { mes: m, total: 0, tipos: {} });

    return arr.map((it, i) => {
      const prev = i > 0 ? arr[i - 1].total : null;
      const growth = prev && prev !== 0 ? ((it.total - prev) / prev) * 100 : null;
      return { ...it, prev, growth };
    });
  }, [rowsRaw, baseYear, tipoSelecionado]);

  return (
    <Paper
      sx={{
        gridColumn: '1 / -1',
        p: 3,
        borderRadius: 3,
        boxShadow: '0px 10px 15px -3px rgba(0,0,0,0.18)',
        height: 420,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography
        variant="h5"
        sx={{ fontWeight: 900, textAlign: 'center', textTransform: 'uppercase', mb: 2 }}
      >
        HISTÓRICO DE PASSAGEIROS
      </Typography>

      {loading && (
        <Typography sx={{ textAlign: 'center', mb: 1, opacity: 0.8 }}>
          Carregando dados de passageiros…
        </Typography>
      )}
      {!loading && error && (
        <Typography color="error" sx={{ textAlign: 'center', mb: 1 }}>
          {error}
        </Typography>
      )}

      <ResponsiveContainer width="100%" height="85%">
        <BarChart
          data={data}
          margin={{ top: 36, right: 28, left: 16, bottom: 8 }}  // + espaço para as %
          barCategoryGap="28%"
          barGap={6}
        >


          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="mes"
            tickFormatter={labelUpper}
            tick={{ fontSize: 12, fill: mode === 'dark' ? '#fff' : '#333' }}
          />
          <YAxis
            tickFormatter={(n) => (n ?? 0).toLocaleString('pt-BR')}
            domain={[0, (max) => (max || 0) * 1.1]}
            width={72}
            tick={{ fontSize: 12, fill: mode === 'dark' ? '#fff' : '#333' }}
          />
          <Tooltip
            content={<CountTooltip />}
            cursor={false}
            wrapperStyle={{ background: 'transparent', border: 'none', boxShadow: 'none' }}
          />
          <Legend
            verticalAlign="top"
            align="right"
            iconType="circle"
            iconSize={8}
            wrapperStyle={{ top: 0 }}
            formatter={(v) => <span style={{ fontSize: 12 }}>{v}</span>}
          />

          <Bar dataKey="prev" name="MÊS ANTERIOR" fill="#01CDFE" radius={[6, 6, 0, 0]}>
            <LabelList content={(p) => <PassengerPrevValueLabel {...p} mode={mode} />} />
            <LabelList content={(p) => <PassengerPrevPctLabel {...p} />} />   {/* <- AQUI */}
          </Bar>

          <Bar dataKey="total" name="MÊS" fill="#05FFA1" radius={[6, 6, 0, 0]}>
            <LabelList content={(p) => <PassengerTotalValueLabel {...p} mode={mode} />} />
            <LabelList content={(p) => <PassengerTotalPctLabel {...p} />} />  {/* <- AQUI */}
          </Bar>

        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
}